// Heatmap "Ventas en salón por franja horaria" — componente React/Tremor.
// Stack del proyecto: React + Tremor (sin dependencias externas extra).
//
// Regla de cálculo (crítica): la participación de cada franja se calcula
// SOBRE EL TOTAL DEL DÍA, nunca sobre el total semanal. La lógica pura vive
// en src/lib/heatmapLogic.ts (testeable sin React).
import { useMemo } from "react";
import { Card, Title, Text } from "@tremor/react";
import {
  computeHeatmap,
  colorFor,
  HeatmapCellInput,
} from "../lib/heatmapLogic";

// ── Dataset de muestra (demo, mientras no haya fuente día×franja real) ──
export const HEATMAP_SAMPLE: HeatmapCellInput[] = [
  { dia_semana: "Lunes", franja_horaria: "10-12", ventas: 80 },
  { dia_semana: "Lunes", franja_horaria: "12-14", ventas: 220 },
  { dia_semana: "Lunes", franja_horaria: "14-16", ventas: 150 },
  { dia_semana: "Lunes", franja_horaria: "16-18", ventas: 200 },
  { dia_semana: "Lunes", franja_horaria: "18-20", ventas: 250 },
  { dia_semana: "Lunes", franja_horaria: "20-22", ventas: 100 },
  { dia_semana: "Martes", franja_horaria: "10-12", ventas: 70 },
  { dia_semana: "Martes", franja_horaria: "12-14", ventas: 210 },
  { dia_semana: "Martes", franja_horaria: "14-16", ventas: 160 },
  { dia_semana: "Martes", franja_horaria: "16-18", ventas: 190 },
  { dia_semana: "Martes", franja_horaria: "18-20", ventas: 260 },
  { dia_semana: "Martes", franja_horaria: "20-22", ventas: 110 },
  { dia_semana: "Miércoles", franja_horaria: "10-12", ventas: 60 },
  { dia_semana: "Miércoles", franja_horaria: "12-14", ventas: 200 },
  { dia_semana: "Miércoles", franja_horaria: "14-16", ventas: 170 },
  { dia_semana: "Miércoles", franja_horaria: "16-18", ventas: 210 },
  { dia_semana: "Miércoles", franja_horaria: "18-20", ventas: 270 },
  { dia_semana: "Miércoles", franja_horaria: "20-22", ventas: 90 },
  { dia_semana: "Jueves", franja_horaria: "10-12", ventas: 90 },
  { dia_semana: "Jueves", franja_horaria: "12-14", ventas: 190 },
  { dia_semana: "Jueves", franja_horaria: "14-16", ventas: 140 },
  { dia_semana: "Jueves", franja_horaria: "16-18", ventas: 220 },
  { dia_semana: "Jueves", franja_horaria: "18-20", ventas: 280 },
  { dia_semana: "Jueves", franja_horaria: "20-22", ventas: 80 },
  { dia_semana: "Viernes", franja_horaria: "10-12", ventas: 50 },
  { dia_semana: "Viernes", franja_horaria: "12-14", ventas: 150 },
  { dia_semana: "Viernes", franja_horaria: "14-16", ventas: 120 },
  { dia_semana: "Viernes", franja_horaria: "16-18", ventas: 240 },
  { dia_semana: "Viernes", franja_horaria: "18-20", ventas: 320 },
  { dia_semana: "Viernes", franja_horaria: "20-22", ventas: 120 },
  { dia_semana: "Sábado", franja_horaria: "10-12", ventas: 100 },
  { dia_semana: "Sábado", franja_horaria: "12-14", ventas: 180 },
  { dia_semana: "Sábado", franja_horaria: "14-16", ventas: 160 },
  { dia_semana: "Sábado", franja_horaria: "16-18", ventas: 200 },
  { dia_semana: "Sábado", franja_horaria: "18-20", ventas: 240 },
  { dia_semana: "Sábado", franja_horaria: "20-22", ventas: 120 },
  { dia_semana: "Domingo", franja_horaria: "10-12", ventas: 110 },
  { dia_semana: "Domingo", franja_horaria: "12-14", ventas: 200 },
  { dia_semana: "Domingo", franja_horaria: "14-16", ventas: 180 },
  { dia_semana: "Domingo", franja_horaria: "16-18", ventas: 190 },
  { dia_semana: "Domingo", franja_horaria: "18-20", ventas: null }, // s/d demo
  { dia_semana: "Domingo", franja_horaria: "20-22", ventas: 120 },
];

// ── Componente ──────────────────────────────────────────
export default function HeatmapFranjaHoraria({ data }: { data: HeatmapCellInput[] }) {
  const h = useMemo(() => computeHeatmap(data), [data]);

  return (
    <Card className="!p-3">
      <Title className="!text-base">Ventas en salón por franja horaria</Title>
      <Text className="!text-xs">% participación sobre el total del mes</Text>

      <div className="mt-3 flex gap-4">
        {/* Matriz */}
        <div className="flex-1 overflow-x-auto">
          <table className="w-full border-collapse" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th className="text-left text-[11px] font-medium text-tremor-content p-1">Mes</th>
                {h.franjas.map((f) => (
                  <th key={f} className="text-center text-[11px] font-medium text-tremor-content p-1 whitespace-nowrap">
                    {f}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {h.dias.map((d) => (
                <tr key={d}>
                  <td className="text-left text-[11px] font-medium text-tremor-content-emphasis p-1 whitespace-nowrap">
                    {d}
                  </td>
                  {h.franjas.map((f) => {
                    const cell = h.matrix[d][f];
                    const { bg, fg } = colorFor(cell.pct, h.min, h.max);
                    return (
                      <td key={f} className="p-0.5">
                        <div
                          className="flex items-center justify-center rounded-sm"
                          style={{
                            background: bg,
                            color: fg,
                            height: 34,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                          title={`${d} ${f}: ${cell.ventas != null ? cell.ventas : "s/d"}${cell.pct != null ? ` (${cell.pct}%)` : ""}`}
                        >
                          {cell.sinDato ? "s/d" : `${cell.pct}%`}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Leyenda vertical */}
        <div className="flex flex-col items-center justify-center shrink-0" style={{ width: 54 }}>
          <span className="text-[10px] text-tremor-content mb-1">Mayor</span>
          <div
            className="rounded-sm"
            style={{
              width: 16,
              height: 120,
              background: "linear-gradient(to bottom, #15335B, #6F91B3)",
            }}
          />
          <span className="text-[10px] text-tremor-content mt-1">Menor</span>
        </div>
      </div>
    </Card>
  );
}
